import * as React from "react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
    Box,
    Container,
    Typography,
    Stack,
    Divider,
    Avatar,
    Paper,
    Link,
} from "@mui/material";

// Hero image
import heroImg from "../assets/images/orchestration/hero.jpg";

// Section images
import section1Img from "../assets/images/orchestration/orchestration-stages.png";
import section2Img from "../assets/images/orchestration/pattern-a.png";
import section5Img from "../assets/images/orchestration/pattern-a.png";

const fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

type Section = {
    number: number;
    title?: string;
    heading?: "h2" | "h3";
    body: React.ReactNode;
};

function CodeBlock({ children }: { children: string }) {
    return (
        <Box
            component="pre"
            tabIndex={0}
            sx={{
                my: 2,
                p: { xs: 2, sm: 2.5 },
                maxWidth: "100%",
                overflowX: "auto",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                bgcolor: "grey.100",
                color: "grey.900",
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
                fontSize: { xs: "0.78rem", sm: "0.88rem" },
                lineHeight: 1.7,
                whiteSpace: "pre",
            }}
        >
            <code>{children}</code>
        </Box>
    );
}

function SectionImage({ src, alt }: { src: string; alt: string }) {
    return (
        <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            sx={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                display: "block",
                my: 2,
            }}
        />
    );
}

function ComparisonTable() {
    const rows = [
        ["Agent autonomy", "Fully preserved", "Preserved through dynamic routing", "Preserved with selective control points"],
        ["Observability", "Coarse, mission/agent level", "Fine-grained, action/tool level", "Configurable by execution path"],
        ["Fault recovery", "Retry whole agent step", "Recover individual actions", "Mix of coarse and fine-grained recovery"],
        ["Durability", "Mission-level", "Step-level", "Applied where needed"],
        ["Complexity", "Low", "High", "Medium–High"],
        ["Flexibility", "High", "High if routing stays dynamic", "High"],
        ["HITL support", "Between agent executions", "Between internal steps", "Native across both styles"],
        ["Best fit", "Simple autonomous agents", "Critical, highly observable flows", "SparrowX production default"],
    ];

    return (
        <Box
            role="region"
            aria-label="Orchestration pattern comparison"
            tabIndex={0}
            sx={{
                my: 2,
                overflowX: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                "& table": { width: "100%", minWidth: 640, borderCollapse: "collapse" },
                "& caption": { textAlign: "left", p: 1.5, fontWeight: 700 },
                "& th, & td": {
                    p: 1.5,
                    textAlign: "left",
                    verticalAlign: "top",
                    borderBottom: 1,
                    borderColor: "divider",
                    fontSize: "0.9rem",
                    lineHeight: 1.6,
                },
                "& thead": { bgcolor: "grey.100", color: "grey.900" },
                "& tbody tr:last-child > *": { borderBottom: 0 },
            }}
        >
            <table>
                <caption>Comparison of orchestration patterns</caption>
                <thead>
                <tr>
                    <th scope="col">Dimension</th>
                    <th scope="col">Pattern A — External</th>
                    <th scope="col">Pattern B — Internal</th>
                    <th scope="col">Hybrid — Composite</th>
                </tr>
                </thead>
                <tbody>
                {rows.map(([dimension, external, internal, hybrid]) => (
                    <tr key={dimension}>
                        <th scope="row">{dimension}</th>
                        <td>{external}</td>
                        <td>{internal}</td>
                        <td>{hybrid}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </Box>
    );
}

function SectionBlock({ section }: { section: Section }) {
    return (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: "flow-root" }}>
                {section.title ? (
                    <Typography
                        variant="h5"
                        component={section.heading ?? "h2"}
                        sx={{
                            fontFamily,
                            fontWeight: 900,
                            letterSpacing: -0.4,
                            lineHeight: 1.25,
                            fontSize: { xs: "1.1rem", sm: "1.3rem" },
                            mb: 1,
                        }}
                    >
                        {section.title}
                    </Typography>
                ) : null}

                <Box
                    sx={{
                        fontFamily,
                        lineHeight: 1.85,
                        fontSize: { xs: "0.98rem", sm: "1.02rem" },
                        "& p": { mt: 0, mb: 2 },
                        "& h3, & h4": {
                            fontFamily,
                            fontWeight: 800,
                            lineHeight: 1.35,
                            letterSpacing: -0.2,
                            mt: 3,
                            mb: 1,
                        },
                        "& h3": { fontSize: { xs: "1.05rem", sm: "1.2rem" } },
                        "& h4": { fontSize: { xs: "1rem", sm: "1.1rem" } },
                        "& ul, & ol": { mt: 0, mb: 2, pl: 3 },
                        "& li": { mb: 1 },
                        "& > :last-child": { mb: 0 },
                    }}
                >
                    {section.body}
                </Box>
            </Box>
        </Paper>
    );
}

function BlogHero() {
    return (
        <Stack spacing={2.25} sx={{ mb: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: 1,
                    borderColor: "divider",
                    mb: 2,
                }}
            >
                <Box
                    sx={{
                        bgcolor: "grey.100",
                        aspectRatio: "16 / 4",
                    }}
                >
                    <Box
                        component="img"
                        src={heroImg}
                        alt="Article hero"
                        loading="lazy"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />
                </Box>
            </Paper>

            <Typography
                variant="h3"
                component="h1"
                sx={{
                    fontFamily,
                    fontWeight: 900,
                    letterSpacing: -0.8,
                    lineHeight: 1.12,
                    fontSize: { xs: "1.3rem", sm: "1.9rem", md: "2.3rem" },
                }}
            >
                From Agent Loops to Durable Agentic Systems
            </Typography>

            <Typography
                variant="h6"
                component="p"
                sx={{
                    fontFamily,
                    fontWeight: 400,
                    color: "text.secondary",
                    lineHeight: 1.5,
                    fontSize: { xs: "1.05rem", sm: "1.15rem" },
                }}
            >
                A SparrowX case study on where agent frameworks end, durable orchestration begins, and how systems like
                Embabel and Temporal work together in production..
            </Typography>

            <Typography
                variant="body2"
                component="p"
                sx={{
                    fontFamily,
                    fontStyle: "italic",
                    fontWeight: 400,
                    color: "text.secondary",
                    lineHeight: 1.55,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                    opacity: 0.9,
                    mt: 1,
                }}
            >
                For the actual Sparrow-X system production code, find it in this{" "}
                <Link
                    href="https://github.com/hashedgrey/Sparrow-x"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                    sx={{ color: "inherit", fontFamily }}
                >
                    Github Repo
                </Link>
                .
            </Typography>

            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
                <Avatar
                    sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "grey.900",
                        color: "common.white",
                        fontFamily,
                        fontWeight: 800,
                    }}
                >
                    AL
                </Avatar>

                <Stack spacing={0.25}>
                    <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
                        <Typography sx={{ fontFamily, fontWeight: 800 }}>
                            Aggrey Lelei
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontFamily, color: "text.secondary" }}
                        > Agentic AI · Agent Orchestration · Durable Execution · AI Infrastructure
                            Distributed Systems · Temporal · Embabel · Human-in-the-Loop
                        </Typography>
                    </Stack>

                    <Typography
                        variant="body2"
                        sx={{ fontFamily, color: "text.secondary" }}
                    >
                        12 min read · September 1, 2026
                    </Typography>
                </Stack>
            </Stack>

            <Divider sx={{ mt: 1 }} />
        </Stack>
    );
}

function BlogClosingNote() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: 3,
                border: 1,
                borderColor: "divider",
                p: { xs: 2.25, sm: 3 },
            }}
        >
            <Stack spacing={1.25}>
                <Typography
                    variant="h6"
                    sx={{
                        fontFamily,
                        fontWeight: 900,
                        letterSpacing: -0.2,
                    }}
                >
                    The Agent Loop Is Only the Beginning
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        fontFamily,
                        color: "text.secondary",
                        lineHeight: 1.75,
                    }}
                >
                    Building an agent is relatively easy. Building an agentic system that can
                    reason, recover, wait, retry, and survive real production failures is a
                    different engineering problem. SparrowX makes that boundary explicit:
                    agenticsvc owns the reasoning and orchestration of the mission, while
                    intsvc and docsvc provide the structured internal knowledge and document
                    evidence the agent acts on. Embabel determines what should happen next;
                    durable orchestration ensures that progress is not lost when the
                    infrastructure underneath it changes or fails. The result is not just an
                    agent loop, but a system capable of carrying that loop reliably from
                    intent to outcome.
                </Typography>
            </Stack>
        </Paper>
    );
}

export default function BlogTemplate() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const sections: Section[] = [
        {
            number: 1,
            body: (
                <>
                    <p>"When I first saw heavyweight orchestration platforms like Temporal or Durable Functions being introduced into agentic architectures, my knee-jerk reaction was skepticism: why stack yet another infrastructure layer onto tools that already manage state and execution?</p>

                    <p>But that perspective stemmed from seeing agents through a lightweight lens—treating them purely as local, single-session tool-callers. The gap becomes obvious the moment an agentic system transitions from a localized script into an enterprise-grade distributed pipeline.</p>

                    <p>Frameworks like Embabel or LangGraph provide the <em>brain</em>—the reasoning loops, memory structures, and tool selection mechanisms. Orchestration engines provide the <em>central nervous system and dynamic memory card</em>—ensuring that when that reasoning loop spans dozens of async microservices, human-in-the-loop gates, or multi-day waits, the entire process is crash-proof, deterministic, and fully replayable."</p>

                    <p>Rather than treating this purely as a theoretical debate, I built <strong>Sparrow-x</strong>: A production-ready agentic architecture to directly address and answer these questions that usually get conflated:</p>

                    <ol>
                        <li><em>If frameworks handle execution loops and tooling out of the box, why stack orchestration on top?</em></li>
                        <li><em>If an engine dictates system state, does the system retain its agentic nature, or is it just a structured workflow?</em></li>
                        <li><em>Where does the framework end and the orchestration engine actually take over?</em></li>
                    </ol>
                </>
            ),
        },
        {
            number: 2,
            title: "Chapter 1: Agent Loop and Agent Orchestration.",
            heading: "h2",
            body: (
                <>
                    <h3>1.1 Definition.</h3>

                    <p>Before diving into question 1, let's get on the same page with a few essential terms that often get blurred together:</p>

                    <p><strong>The Agent Loop (The Cognitive Mechanics):</strong> The core reasoning pattern of the application. It dictates how the model processes context, decides whether to plan versus execute, invokes tools, and determines its terminal condition. This logic is the defining essence of an agent.</p>

                    <p><strong>The Agent Framework (The Engineering Scaffolding):</strong> The application-level developer layer (e.g. Embabel, LangGraph, Strands). It wraps the raw reasoning loop in enterprise infrastructure—handling tool registration, memory ingestion, tracing, structured logging, and multi-agent dispatch. Its primary role is velocity: taking a local reasoning loop and turning it into production-ready software quickly.</p>

                    <p><strong>The Orchestration Engine (The Infrastructure Layer):</strong> The underlying execution backbone (e.g., Temporal, Durable Functions). It offloads execution state entirely to handle distributed, system-level challenges: surviving host crashes, managing multi-day human-in-the-loop pauses, coordinating multi-agent workflows, and guaranteeing execution durability across process boundaries.</p>

                    <p>Frameworks accelerate feature delivery and lower the entry barrier for building AI applications; orchestration engines guarantee operational resilience and state preservation under load. Rather than being mutually exclusive, they form a symbiotic technical stack.</p>

                    <p>Consider a real-world enterprise agentic task managed via Sparrow-x: resolving an internal inquiry might demand dozens of sequential LLM reasoning steps, queries across a company directory, deep dives into internal research repositories, multiple tool executions, and multi-day pauses waiting for human administrative sign-off. Every single point in this chain introduces network latency, potential API timeouts, or transient infrastructure faults.</p>

                    <p>While application frameworks provide convenient local retry logic, they are fundamentally ill-equipped to preserve state across node crashes, survive Kubernetes pod evictions, or maintain execution history during extended multi-day waits. Orchestration engines bridge this exact reliability deficit—offloading state management from application memory to durable infrastructure so complex workflows run to completion regardless of underlying platform instability.</p>

                    <h3>1.2 Boundary Between an Orchestration Engine and an Agent.</h3>

                    <p>The underlying distinction boils down to where non-determinism lives within the architecture. True agency does not require an unpredictable system hierarchy; rather, it requires that the model retains real-time control over action selection within defined goal boundaries. Frameworks like Embabel leverage Goal-Oriented Action Planning (GOAP) precisely to enable this: the system dynamically evaluates available actions, environment state, and target goals to resolve its own execution path autonomously.</p>

                    <p>When an orchestration engine wraps an agentic system, it does not strip away this autonomy or collapse the process into a rigid, step-by-step pipeline like Step 1 -&gt; Step 2 -&gt; Step 3. Instead, the orchestration layer acts as a durable host for the non-deterministic reasoning loop. The LLM—guided by GOAP mechanics—continues to dynamically select tools, iterate on intermediate results, and evaluate progression toward the goal, while the orchestration engine simply records each decision point to guarantee that the agent's emergent control flow can survive system failure without losing its place.</p>

                    <h3>The 3 Orchestration Patterns.</h3>

                    <h4>Pattern A — External Orchestration in SparrowX</h4>

                    <SectionImage src={section1Img} alt="External orchestration: Temporal hosts the SparrowX agent loop" />

                    <p>Temporal sits outside the Embabel agent and treats each agent execution as one durable workflow step. It manages sequencing, retries, recovery, waits, and human approval without controlling the agent’s internal reasoning.</p>

                    <p>Temporal Workflow</p>

                    <CodeBlock>{`Step 1: Invoke SparrowX AgenticService
        [intent → planning → tools → review → result]
Step 2: Wait for approval / external event
Step 3: Resume or invoke another agent execution
Step 4: Return the final mission result`}</CodeBlock>

                    <p>Inside agenticsvc, Embabel remains responsible for the reasoning loop. It can plan dynamically, call intsvc, query docsvc, use LLMs, review results, and decide when the task is complete.</p>

                    <p>Temporal only sees the higher-level execution state: started, waiting, retried, failed, or completed. This preserves agent autonomy, but gives Temporal relatively coarse observability unless SparrowX explicitly exposes internal checkpoints.</p>

                    <p>For SparrowX, the boundary is simple: <strong>Embabel owns the agent loop; Temporal makes that loop durable.</strong></p>

                    <h4>Pattern B — Internal Orchestration in SparrowX</h4>

                    <SectionImage src={section2Img} alt="Internal orchestration: durable boundaries within the SparrowX agent loop" />

                    <p>Pattern B moves durability <strong>inside the agent execution</strong>. Instead of Temporal treating the whole Embabel agent as one opaque operation, important reasoning and tool-execution boundaries become individually recoverable steps.</p>

                    <p>Temporal + Embabel</p>

                    <CodeBlock>{`Step 1: Interpret mission
Step 2: Build / update plan
Step 3: Execute intsvc or docsvc capability
Step 4: Evaluate resulting evidence
Step 5: Continue, re-plan, or synthesize
        ↳ next step determined dynamically`}</CodeBlock>

                    <p>At first this can resemble a conventional workflow: intent → planning → tool execution → synthesis. But SparrowX is not simply executing a fixed sequence. Its MissionAgent is already modeled as an Embabel graph whose route is derived from typed states—MissionRunInput → IntentState → PlanState → MissionEvidence → MissionResult—rather than a hard-coded pipeline.</p>

                    <p>The agent also works against capabilities such as document evidence, internal entity search, governance and synthesis rather than one predetermined tool chain. Embabel therefore retains responsibility for deciding which valid action becomes reachable next.</p>

                    <p>Pattern B changes <strong>where the durability boundary sits</strong>. Instead of recovering an entire mission-level agent invocation, Temporal can persist progress around smaller agent actions. If execution fails after evidence retrieval, SparrowX can recover from that boundary rather than repeating the whole reasoning run.</p>

                    <p>So Pattern B does not turn SparrowX into a static workflow. <strong>Embabel still determines the agent path; Temporal makes more of that path durable and observable.</strong></p>

                    <h4>Pattern 3 — Hybrid</h4>

                    <p>SparrowX combines both styles in production: autonomous agents can run as durable black-box steps, while critical operations are exposed as smaller recoverable actions. Human approval can pause either flow, giving Temporal control and durability without reducing Embabel’s agent autonomy.</p>

                    <p>Temporal Workflow — SparrowX Composite Pattern</p>

                    <CodeBlock>{`┌─ Step 1: Interpret / Plan Mission ──────────────────────────┐
│ Fine-grained execution (Pattern B)                         │
│ Embabel: intent → planning → next action                   │
└───────────────────────────────────────────────────────────┘
                              ↓
┌─ Step 2: Invoke Specialist Agent ──────────────────────────┐
│ Black-box execution (Pattern A)                           │
│ Agent runs its own Embabel reasoning loop internally      │
└───────────────────────────────────────────────────────────┘
                              ↓
┌─ Step 3: Wait for Human Approval ──────────────────────────┐
│ Temporal durable wait / signal                            │
│ Mission can pause and resume without restarting           │
└───────────────────────────────────────────────────────────┘
                              ↓
┌─ Step 4: Continue Fine-Grained Agent Execution ─────────────┐
│ Pattern B                                                 │
│ Planning, intsvc/docsvc calls, review and synthesis         │
│ can be checkpointed and recovered independently           │
└───────────────────────────────────────────────────────────┘
                              ↓
┌─ Step 5: Complete Mission ─────────────────────────────────┐
│ Build grounded result + citations                         │
│ Temporal records completion and final workflow state      │
└───────────────────────────────────────────────────────────┘`}</CodeBlock>

                    <ComparisonTable />
                </>
            ),
        },
        {
            number: 3,
            title: "Chapter 2: Core Mechanism of Durable Orchestration in SparrowX",
            heading: "h2",
            body: (
                <>
                    <p>Chapter 1 covered how Temporal and Embabel can work together without reducing SparrowX agents to fixed workflows. The next question is what Temporal contributes beneath the agent runtime.</p>

                    <h3>Step → Persist → Resume</h3>

                    <p>At its core, durable execution separates agent progress from the life of a single process. Important operations are recorded outside the running service so a SparrowX mission can recover without starting again from the beginning.</p>

                    <h4>① Step — define durable boundaries</h4>

                    <p>SparrowX identifies meaningful operations such as agent actions, intsvc or docsvc calls, human-approval waits, and other external work. In Temporal, these boundaries are typically represented by Workflow operations and Activities.</p>

                    <h4>② Persist — record completed work</h4>

                    <p>Temporal records workflow decisions and Activity outcomes in its durable Event History. SparrowX can additionally persist application-level mission checkpoints in PostgreSQL where richer agent state needs to survive independently of the workflow engine.</p>

                    <p>This separates two concerns: Temporal preserves execution history; SparrowX preserves domain and agent state.</p>

                    <h4>③ Replay — reconstruct and continue</h4>

                    <p>After a worker crash, restart, or long pause, Temporal rebuilds the Workflow from its Event History. Operations that already completed are resolved from recorded history rather than executed again, and execution continues from the outstanding work.</p>

                    <p>For SparrowX, this means a completed docsvc retrieval, intsvc lookup, or other durable Activity does not need to be repeated simply because the worker disappeared. Embabel can continue operating from the recovered mission state while Temporal restores the execution path around it.</p>

                    <CodeBlock>{`The resulting model is:

Embabel decides what the agent should do
            ↓
Temporal executes durable boundaries
            ↓
Event History records execution progress
            ↓
SparrowX checkpoints preserve agent/domain state
            ↓
Failure
            ↓
Replay history + restore state
            ↓
Continue remaining work`}</CodeBlock>

                    <p>The important distinction is that replay is not rerunning the whole agent. Temporal reconstructs completed workflow progress from history, allowing SparrowX to resume from a durable boundary while avoiding unnecessary repeated tool calls, side effects, and LLM usage.</p>
                </>
            ),
        },
        {
            number: 4,
            title: "Scenario 1: Crash Recovery — Resume Instead of Restarting",
            heading: "h3",
            body: (
                <>
                    <p>Suppose a SparrowX mission has been running for 20 minutes, finishes eight durable operations, then the worker fails during the ninth.</p>

                    <p><strong>Without durable orchestration:</strong> SparrowX would need to restart the mission, reconstruct execution from stored traces, or implement its own recovery system. Restarting may repeat LLM calls, intsvc/docsvc requests, and other expensive work.</p>

                    <p><strong>With Temporal:</strong> completed Activities are already represented in Workflow history. During recovery, Temporal replays the Workflow using those recorded results and continues from the unfinished operation instead of recomputing everything that came before it.</p>

                    <CodeBlock>{`Steps 1–8 completed
        ↓
Worker crashes during Step 9
        ↓
Temporal replays Workflow history
        ↓
Steps 1–8 resolve from recorded results
        ↓
Step 9 resumes / retries`}</CodeBlock>

                    <p>This is fundamentally different from restoring an agent conversation and asking the LLM to continue. A trace helps the model understand <strong>what happened</strong>; durable execution tells the runtime <strong>what has already completed</strong>.</p>

                    <p>Two differences matter in SparrowX:</p>

                    <ol>
                        <li><strong>Avoid unnecessary LLM and tool cost</strong> — if an LLM or service call completed as a recorded Activity, recovery does not need to invoke it again simply to reconstruct progress.</li>
                        <li><strong>Infrastructure-level recovery</strong> — Temporal determines completed work from Workflow history rather than relying on Embabel or the LLM to infer which operations should be repeated.</li>
                    </ol>

                    <p>There is one important production detail: external side effects should still be <strong>idempotent</strong>. An Activity can be retried if failure occurs around the boundary between performing the external operation and recording its completion. Temporal provides durable recovery; idempotency prevents a retry from creating duplicate effects.</p>

                    <p>For SparrowX, the result is simple: <strong>a process failure should interrupt a mission, not erase its progress.</strong></p>
                </>
            ),
        },
        {
            number: 5,
            title: "Scenario 2: Long-Running Missions — Outliving the Worker",
            heading: "h3",
            body: (
                <>
                    <p>SparrowX missions should not depend on one JVM, pod, or worker remaining alive for their entire duration. Research tasks, approval-driven workflows, and other long-running missions may continue far beyond the lifetime of the process that started them.</p>

                    <p>The solution is to keep <strong>execution state outside the worker</strong>. Temporal stores durable workflow history, while SparrowX can persist richer mission and checkpoint state in PostgreSQL. Embabel workers then become replaceable execution hosts rather than owners of mission progress.</p>

                    <CodeBlock>{`Temporal + SparrowX Durable State

Worker A                  Worker B                  Worker C
────────                  ────────                  ────────
Execute Steps 1–3    →     Replay history       →    Replay history
Persist progress          Continue Steps 4–6        Continue Step 7
      ↓                         ↓                         ↓
Pod terminated            HITL wait: 3 days          Mission complete`}</CodeBlock>

                    <p>If one worker disappears, another can continue the same Temporal Workflow using recorded history instead of restarting the mission. A human-approval wait can also remain durable for hours or days without keeping the original worker alive.</p>

                    <p>The key architectural shift is that <strong>the process becomes temporary, while the mission remains durable</strong>. Embabel continues to drive agent execution, but Temporal ensures that execution can survive worker restarts, scaling events, deployments, and infrastructure failures.</p>
                </>
            ),
        },
        {
            number: 6,
            title: "Scenario 3: Human-in-the-Loop — Durable Waiting Without Holding a Worker",
            heading: "h3",
            body: (
                <>
                    <p>SparrowX missions may need human input before continuing: approving a sensitive action, validating evidence, or resolving an escalation. Those pauses can last hours or days, far longer than any individual worker should remain occupied.</p>

                    <p><strong>Without durable orchestration:</strong> SparrowX would need to serialize mission state, persist it, release the process, receive an external callback, restore the state, and reconstruct execution manually.</p>

                    <p><strong>With Temporal:</strong> the Workflow can enter a durable wait using a Signal, Update, timer, or Workflow.await. Temporal records the waiting state while the worker is released. No JVM thread or pod needs to remain blocked while approval is pending.</p>

                    <CodeBlock>{`SparrowX executes Steps 1–3
            │
            ▼
Step 4: Request Human Approval
            │
            ├── Temporal records waiting state
            │   Worker is released
            │
            │   ... 3 days pass ...
            │
            ├── Approval Signal received
            │
            ▼
Temporal restores Workflow state
            │
            ▼
Embabel continues from the approved path
            │
            ▼
Step 5 onward`}</CodeBlock>

                    <p>Completed work is reconstructed from Temporal's Workflow history, while SparrowX's persisted mission state remains available to Embabel. The approval event simply unlocks the next valid part of the mission.</p>

                    <p>The result is that <strong>a SparrowX mission can wait for human input for days without keeping an agent process alive</strong>, then continue from the same durable execution state once the response arrives.</p>
                </>
            ),
        },
        {
            number: 7,
            title: "Scenario 4: Stability — Isolate Failures and Retry Only What Broke",
            heading: "h3",
            body: (
                <>
                    <p>SparrowX depends on external systems such as LLM providers, intsvc, docsvc, databases, and other APIs. At production scale, transient failures are expected. The goal is to stop one failed call from invalidating the entire mission.</p>

                    <p><strong>Without durable orchestration:</strong> retry logic ends up scattered throughout agent code—backoff, retryability checks, attempt limits, and recovery rules mixed into tool and service implementations.</p>

                    <p><strong>With Temporal:</strong> Activities can use declarative retry policies. If one external call fails, Temporal retries that Activity independently while previously completed work remains intact.</p>

                    <CodeBlock>{`Step 1 ✔ → Step 2 ✔ → Step 3 ✘ ↻ ↻ ✔ → Step 4 ✔ → Done`}</CodeBlock>

                    <p>For SparrowX, this keeps infrastructure concerns outside Embabel's reasoning logic. Embabel decides what action should happen next; Temporal decides how that action should survive transient infrastructure failure.</p>

                    <p>The result is <strong>localized recovery instead of mission-wide failure</strong>: a temporary docsvc, intsvc, database, or LLM error can be retried without replaying unrelated work.</p>
                </>
            ),
        },
        {
            number: 8,
            title: "Scenario 5: Observability — Event History + Visibility + LLM Tracing",
            heading: "h3",
            body: (
                <>
                    <p>Most teams today rely on tracing tools (Langfuse, Braintrust) for agent observability — tracking prompts, completions, token usage, and latency. This is valuable, but in my view it's only half the picture. Production agent observability should have two complementary layers:</p>

                    <p><strong>Layer 1: Execution-level observability — from the orchestration engine (Event History + Visibility)</strong></p>

                    <p>Event History records every step's start/complete/fail/retry, Signal send/receive, timing, and return values — a complete audit trail of execution flow. On top of this, a Visibility layer provides SQL-like queries across all workflows (e.g., "find all Failed workflows for customer C123"). This tells you <em>what happened</em> during execution: which steps ran, which failed, how long the wait was, how many retries occurred. (<Link href="https://docs.temporal.io/workflow-execution/event" target="_blank" rel="noopener noreferrer" underline="always">Temporal Events Docs</Link>)</p>

                    <p><strong>Layer 2: Inference-level observability — from LLM tracing tools (Langfuse/Braintrust)</strong></p>

                    <p>This records prompt/completion content, token usage + cost, latency, and evaluation scores. This tells you <em>how the agent reasoned</em>: what it was asked, what it answered, how much it cost, and whether the output quality was good. It does NOT record workflow execution state, retry logic, or Signal handling.</p>

                    <p><strong>Connecting the two layers</strong></p>

                    <p>The two layers can be connected via OpenTelemetry and shared trace_id, enabling full-stack agent observability:</p>

                    <ul>
                        <li>Agent output quality is poor → check Langfuse for prompt/completion to debug reasoning</li>
                        <li>Agent execution stuck/failed → check Temporal UI for execution state and retry logs</li>
                        <li>Jump between them via trace correlation — from Temporal's Activity directly into Langfuse to see the specific LLM call content</li>
                    </ul>

                    {/*<SectionImage src={section5Img} alt="Execution and inference observability connected through trace correlation" />*/}
                </>
            ),
        },
    ];

    return (
        <>
            <Helmet>
                <title>From Agent Loops to Durable Agentic Systems</title>

                <meta
                    name="description"
                    content="A SparrowX case study on where agent frameworks end, durable orchestration begins,
                    and how Embabel and Temporal work together to build resilient production agentic systems."
                />

                <link
                    rel="canonical"
                    href="https://aggreylelei.com/blog/agentic-architecture"
                />

                <meta property="og:type" content="article" />
                <meta
                    property="og:title"
                    content="From Agent Loops to Durable Agentic Systems"
                />
                <meta
                    property="og:description"
                    content="A SparrowX case study on where agent frameworks end, durable orchestration begins,
                    and how Embabel and Temporal work together to build resilient production agentic systems."
                />
                <meta
                    property="og:url"
                    content="https://aggreylelei.com/blog/agentic-architecture"
                />
                <meta
                    property="og:image"
                    content="https://aggreylelei.com/og-agentic.jpg"
                />

                <meta property="article:published_time" content="2026-09-01T09:00:00+03:00" />
                <meta property="article:author" content="Aggrey Lelei" />
                <meta property="og:site_name" content="Aggrey Lelei" />

                <meta name="author" content="Aggrey Lelei" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="From Agent Loops to Durable Agentic Systems"
                />
                <meta
                    name="twitter:description"
                    content="A SparrowX case study on where agent frameworks end, durable orchestration begins, and how Embabel and Temporal work together to build resilient production agentic systems."
                />
                <meta
                    name="twitter:image"
                    content="https://aggreylelei.com/og-agentic.jpg"
                />
            </Helmet>

            <Box
                component="main"
                sx={{
                    fontFamily,
                    bgcolor: "background.default",
                    color: "text.primary",
                    py: { xs: 5, md: 8 },
                    position: "relative",
                    zIndex: 0,
                }}
            >
                <Container maxWidth="md">
                    <BlogHero />

                    <Stack spacing={3.25} sx={{ fontFamily }}>
                        {sections.map((section) => (
                            <SectionBlock
                                key={section.number}
                                section={section}
                            />
                        ))}

                        <Divider />
                        <BlogClosingNote />
                    </Stack>
                </Container>
            </Box>
        </>
    );
}